import { ModelType, ModelParams, ColabfoldParams, RFDiffusionParams, ESMIFParams } from '@/types/colabfold';
import api from '@/services/api';
import { AxiosError } from 'axios';
import axios from 'axios';
import { Job } from '../types/job';

interface ApiErrorResponse {
  error: string;
}

export const createJob = async (modelType: ModelType, params: ModelParams) => {
  try {
    // Extract jobname and folder from params and structure the request properly
    const { jobname, folder, ...otherParams } = params;
    const requestData = {
      jobname,
      folder,
      params: otherParams
    };
    
    const response = await api.post(`/api/${modelType}/jobs/`, requestData);
    return response.data;
  } catch (error) {
    console.error(`Error creating ${modelType} job:`, error);
    // Extract error message from response if available
    if (error instanceof AxiosError && error.response?.data?.error) {
      throw new Error((error.response.data as ApiErrorResponse).error);
    }
    throw error;
  }
};

export const createColabfoldJob = async (params: ColabfoldParams) => {
  return createJob(ModelType.COLABFOLD, params);
};

export const createRFDiffusionJob = async (params: RFDiffusionParams) => {
  return createJob(ModelType.RFDIFFUSION, params);
};

export const createESMIFJob = async (params: ESMIFParams) => {
  return createJob(ModelType.ESMIF, params);
};

export const getJobs = async (modelType: ModelType) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${modelType} jobs:`, error);
    throw error;
  }
};

export const getAllJobs = async () => {
  try {
    const response = await api.get('/api/jobs/');
    return response.data;
  } catch (error) {
    console.error('Error fetching all jobs:', error);
    throw error;
  }
};

export const getJobStatus = async (modelType: ModelType, jobId: string) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/${jobId}/status/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching status for ${modelType} job ${jobId}:`, error);
    throw error;
  }
};

export const getJobLogs = async (modelType: ModelType, jobId: string) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/${jobId}/logs/`);
    return response.data;
  } catch (error) {
    // If it's a 404 error (NoSuchKey), return null instead of throwing
    if (error instanceof AxiosError && error.response?.status === 404) {
      console.warn(`No logs found for ${modelType} job ${jobId}`);
      return { logs: null };
    }
    // For other errors, log and rethrow
    console.error(`Error fetching logs for ${modelType} job ${jobId}:`, error);
    throw error;
  }
};

export const getJobFiles = async (modelType: ModelType, jobId: string) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/${jobId}/files/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching files for ${modelType} job ${jobId}:`, error);
    throw error;
  }
};

export const deleteJob = async (modelType: ModelType, jobId: string) => {
  try {
    // Add a timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await api.delete(`/api/${modelType}/jobs/${jobId}/?_t=${timestamp}`);
    
    // Check if the response is HTML (which would indicate an error)
    if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
      console.error('Received HTML response for delete request');
      throw new Error('Invalid response from server. Please try again later.');
    }
    
    return response.data;
  } catch (error: unknown) {
    // Check if the error response is HTML (CloudFront returning index.html)
    if (
      error && 
      typeof error === 'object' && 
      'response' in error && 
      error.response && 
      typeof error.response === 'object' && 
      'data' in error.response && 
      typeof error.response.data === 'string' && 
      error.response.data.includes('<!DOCTYPE html>')
    ) {
      console.error('Received HTML response for delete request - CloudFront routing issue');
      
      // If we got HTML back but the status is 200, it's likely CloudFront redirected a 403/404 to index.html
      if (
        'status' in error.response && 
        error.response.status === 200
      ) {
        // Assume the delete was successful despite CloudFront's redirection
        console.warn('Assuming delete was successful despite CloudFront returning HTML');
        return { success: true, message: 'Job deleted (assumed successful)' };
      }
    }
    
    console.error(`Error deleting ${modelType} job ${jobId}:`, error);
    throw error;
  }
};

export const downloadFile = async (modelType: ModelType, jobId: string, filename: string, downloadFilename?: string) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/${jobId}/download/?file=${filename}`, {
      responseType: 'blob',
      // Add timeout to prevent hanging requests
      timeout: 30000,
      // Add headers to ensure proper content handling
      headers: {
        'Accept': '*/*',
      }
    });
    
    // Check if the response is valid
    if (response.data.size === 0) {
      throw new Error('Empty file received');
    }
    
    // Create a download link and trigger it
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', downloadFilename || filename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
    
    return response.data;
  } catch (error) {
    console.error(`Error downloading file ${filename} for ${modelType} job ${jobId}:`, error);
    
    // Handle specific error types
    if (error instanceof Error) {
      if (error.message.includes('contentlength') || error.message.includes('network error')) {
        throw new Error(`Network error while downloading file. Please try again later.`);
      }
    }
    
    throw error;
  }
};

export const downloadAllAsZip = async (modelType: ModelType, jobId: string, jobName?: string) => {
  try {
    const response = await api.get(`/api/${modelType}/jobs/${jobId}/download/?zip=true`, {
      responseType: 'blob',
      // Add timeout to prevent hanging requests
      timeout: 60000,
      // Add headers to ensure proper content handling
      headers: {
        'Accept': '*/*',
      }
    });

    // Check if the response is valid
    if (response.data.size === 0) {
      throw new Error('Empty zip file received');
    }

    // Create a download link and trigger it
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Use job name in zip filename if available
    const zipFilename = jobName 
      ? `${jobName.replace(/[^a-zA-Z0-9]/g, '_')}_files.zip`
      : `job-${jobId}-files.zip`;
      
    link.setAttribute('download', zipFilename);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

    return response.data;
  } catch (error) {
    console.error(`Error downloading zip for ${modelType} job ${jobId}:`, error);
    
    // Handle specific error types
    if (error instanceof Error) {
      if (error.message.includes('contentlength') || error.message.includes('network error')) {
        throw new Error(`Network error while downloading zip. Please try again later.`);
      } else if (error.message.includes('400')) {
        throw new Error(`Server cannot create zip file. Some files may be missing or corrupted.`);
      }
    }
    
    throw error;
  }
};

export const getJob = async (id: string): Promise<{ data: Job }> => {
  const response = await axios.get(`/api/jobs/${id}/`);
  return response.data;
}; 